import React from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, CheckCircle2, Clock, Loader2 } from 'lucide-react';
import { useDocumentStore } from '@/stores/useDocumentStore';
import { withErrorBoundary } from '@/components/error-boundary/withErrorBoundary';
import { hasPendingEntityScreening, potentialMatchNames, selectVerificationOutcome, unreadNameCount, verificationOutcomeLabel } from '@/domain/document-processing/utils/verification-outcome';
import { latestHumanReviews } from '@/types/identity-review-types';
import { verificationTimeLabel, verificationTimingScope } from '@/types/verification-timing';

interface SummaryEntity {
  name?: string;
  type?: string;
  reviewStatus?: string;
  complianceStatus?: string;
  addresses?: unknown[];
  contacts?: unknown[];
  variations?: SummaryEntity[];
}

function ProcessingSummaryComponent() {
  const state = useDocumentStore();
  const { consolidationResult, extractedEntities, results, showConsolidatedEntities, documentMetadata } = state;
  const headingId = React.useId();
  const outcome = selectVerificationOutcome(state);
  // Use the same population as the entity table, including extracted-only results.
  const consolidated = consolidationResult?.consolidatedEntities ?? [];
  const isConsolidated = showConsolidatedEntities && consolidated.length > 0;
  const entities: SummaryEntity[] = isConsolidated ? consolidated : extractedEntities;
  // The same list that decides the outcome, so the headline and the chips always agree with it.
  const matchNames = potentialMatchNames(state);
  const stats = React.useMemo(() => entities.reduce((counts, entity) => {
    const type = typeof entity.type === 'string' ? entity.type.toLowerCase() : '';
    if (type === 'organization') counts.organisations++;
    if (type === 'individual') counts.people++;
    counts.addresses += Array.isArray(entity.addresses) ? entity.addresses.length : 0;
    counts.contacts += Array.isArray(entity.contacts) ? entity.contacts.length : 0;
    const status = entity.reviewStatus ?? entity.complianceStatus;
    const screeningPending = hasPendingEntityScreening(entity);
    if (screeningPending) counts.pending++;
    if (status === 'flagged') counts.flagged++;
    // Blank names are marked approved without a screening call, as the parties table shows.
    else if (status === 'approved' && entity.name?.trim() && !screeningPending) counts.approved++;
    else if (!screeningPending) counts.unreviewed++;
    return counts;
  }, { organisations: 0, people: 0, addresses: 0, contacts: 0, approved: 0, flagged: 0, pending: 0, unreviewed: 0 }), [entities]);

  const humanReviews = React.useMemo(() => latestHumanReviews([
    ...(consolidationResult?.humanIdentityReviews ?? []),
    ...results.flatMap(result => result.humanIdentityReviews ?? []),
  ]), [consolidationResult?.humanIdentityReviews, results]);
  const unresolvedNames = unreadNameCount(state);
  const pendingScreenings = humanReviews.filter(review => review.screening.status === 'pending').length;
  const flaggedHumanNames = humanReviews.filter(review => review.screening.status === 'flagged' || review.retainedScreening).length;
  const sourceFiles = consolidationResult?.metadata.sourceFiles;
  const resultFileCount = new Set(results.map(result => result.documentId)).size;
  const fileCount = sourceFiles?.length || resultFileCount || documentMetadata.totalFiles;
  const timing = consolidationResult?.metadata.verificationTiming;
  const timeLabel = verificationTimeLabel(timing);
  const consolidationMs = consolidationResult?.metadata.processingTime;
  const errors = documentMetadata.processingErrors ?? [];
  const OutcomeIcon = outcome === 'running' ? Loader2 : outcome === 'complete' ? CheckCircle2 : outcome === 'unknown' ? Clock : AlertTriangle;
  const outcomeTone = outcome === 'incomplete' || outcome === 'failed' ? 'text-destructive' : 'text-primary';
  const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;
  // Some parties have no screening result: say so in the headline instead of claiming everything was screened.
  const unscreenedNote = outcome === 'complete' && stats.unreviewed > 0
    ? `${plural(stats.unreviewed, 'party has', 'parties have')} no screening result; see below.` : null;
  const outcomeLabel = outcome === 'complete' && unscreenedNote ? 'Processing complete' : verificationOutcomeLabel[outcome];
  const filesDone = `All ${plural(fileCount, 'file', 'files')} processed${unscreenedNote ? '.' : ' and screened.'}`;
  const outcomeDetail = outcome === 'running' ? 'Results will update as verification progresses.'
    : outcome === 'complete' ? (unscreenedNote ? `${filesDone} ${unscreenedNote}` : filesDone)
    : outcome === 'unknown' ? 'This saved order has no recorded verification outcome. Inspect the available results.'
    : 'Some steps did not finish. The results below are kept; see the notes underneath.';
  const breakdown = [
    plural(stats.organisations, 'organisation', 'organisations'), plural(stats.people, 'person', 'people'),
    plural(stats.addresses, 'address', 'addresses'), plural(stats.contacts, 'contact', 'contacts'),
  ].join(' · ');

  React.useEffect(() => {
    if (entities.length) Sentry.addBreadcrumb({
      category: 'processing', message: 'Processing summary updated', level: 'info',
      data: { totalEntities: entities.length, approvedEntities: stats.approved, flaggedEntities: stats.flagged, outcome },
    });
  }, [entities.length, stats.approved, stats.flagged, outcome]);

  return (
    <section aria-labelledby={headingId} className="rounded-lg border bg-card text-card-foreground">
      <div className="space-y-4 p-4 sm:p-5">
        <h2 id={headingId} className="text-base font-semibold">Verification summary</h2>
        <div className="flex items-start gap-2.5" role="status">
          <OutcomeIcon aria-hidden="true" className={`mt-0.5 h-5 w-5 shrink-0 ${outcome === 'running' ? 'animate-spin motion-reduce:animate-none' : ''} ${outcomeTone}`} />
          <div className="min-w-0">
            <p className="font-medium">{outcomeLabel}</p>
            <p className="mt-1 text-sm text-muted-foreground">{outcomeDetail}</p>
          </div>
        </div>

        {outcome === 'complete' && (matchNames.length > 0
          ? <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3" role="alert">
            <p className="text-lg font-semibold text-destructive">{plural(matchNames.length, 'potential sanctions match', 'potential sanctions matches')}</p>
            <ul className="mt-2 flex flex-wrap gap-2" aria-label="Parties with potential matches">
              {matchNames.map(name => <li key={name} className="rounded-md border border-destructive/40 bg-background px-2.5 py-1 text-sm font-medium text-destructive">{name}</li>)}
            </ul>
            <p className="mt-2 text-sm text-muted-foreground">Review each match in the parties list below.</p>
          </div>
          : <p className="text-sm font-medium">No potential sanctions matches among the screened parties.</p>)}

        <dl className="grid grid-cols-3 gap-x-6 gap-y-4 border-y py-4">
          <div><dt className="text-xs text-muted-foreground">Files</dt><dd className="mt-1 text-xl font-semibold tabular-nums">{fileCount || 'Unavailable'}</dd></div>
          <div><dt className="text-xs text-muted-foreground">Parties</dt><dd className="mt-1 text-xl font-semibold tabular-nums">{entities.length}</dd></div>
          <div><dt className={`text-xs ${stats.flagged > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>Potential matches</dt><dd className={`mt-1 text-xl font-semibold tabular-nums ${stats.flagged > 0 ? 'text-destructive' : ''}`}>{stats.flagged}</dd></div>
        </dl>

        <div className="space-y-1 text-sm text-muted-foreground">
          <p>{breakdown}</p>
          {stats.pending > 0 && <p className="text-destructive">{plural(stats.pending, 'party has', 'parties have')} not finished screening.</p>}
          {stats.unreviewed > 0 && !unscreenedNote && <p>{plural(stats.unreviewed, 'party has', 'parties have')} no recorded screening result.</p>}
        </div>

        {(unresolvedNames > 0 || humanReviews.length > 0) && <div className="space-y-1 border-t pt-3 text-sm text-muted-foreground">
          {unresolvedNames > 0 && <p>{plural(unresolvedNames, 'name', 'names')} could not be read automatically, so {unresolvedNames === 1 ? 'it was' : 'they were'} not screened. Check {unresolvedNames === 1 ? 'it' : 'them'} in the parties list below if needed.</p>}
          {humanReviews.length > 0 && <p>{plural(humanReviews.length, 'name', 'names')} confirmed by a reviewer and screened.</p>}
          {pendingScreenings > 0 && <p className="text-destructive">{plural(pendingScreenings, 'confirmed name has', 'confirmed names have')} not finished screening.</p>}
          {flaggedHumanNames > 0 && <p className="text-destructive">{plural(flaggedHumanNames, 'confirmed name has a', 'confirmed names have')} potential sanctions {flaggedHumanNames === 1 ? 'match' : 'matches'}.</p>}
        </div>}

        {errors.length > 0 && <ul className="list-disc space-y-1 pl-5 text-sm text-destructive">{errors.map((error, index) => <li key={index}>{error}</li>)}</ul>}
        <p className="border-t pt-3 text-xs text-muted-foreground" title={verificationTimingScope}>
          {timeLabel !== 'Unavailable' ? `Took ${timeLabel} from start to result.`
            : `Total time was not recorded.${typeof consolidationMs === 'number' && Number.isFinite(consolidationMs) && consolidationMs >= 0 ? ` Consolidation took ${(consolidationMs / 1000).toFixed(1)}s.` : ''}`}
        </p>
      </div>
    </section>
  );
}

export const ProcessingSummary = withErrorBoundary(ProcessingSummaryComponent, { feature: 'processing-summary' });
